import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { orderApi } from "../api/orderApi";
import AdminSidebar from "../components/AdminSidebar";
import Message from "../components/Message";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({ status: "pending", estimated_arrival: "" });
  const [notice, setNotice] = useState(null);
  const [shipment, setShipment] = useState(undefined);

  useEffect(() => {
    orderApi.getAdminOrder(id).then((data) => {
      const current = data.order || data;
      setOrder(current);
      setForm({
        status: current.status || "pending",
        estimated_arrival: current.estimated_arrival ? String(current.estimated_arrival).slice(0, 16) : "",
      });
    }).catch((error) => setNotice({ type: "error", text: error.message }));
    orderApi.getShipment(id).then((data) => setShipment(data.shipment)).catch(() => setShipment(null));
  }, [id]);

  async function handleCreateShipment() {
    try {
      await orderApi.createShipment(id);
      const data = await orderApi.getShipment(id);
      setShipment(data.shipment);
      setNotice({ type: "success", text: "Shipment created successfully!" });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  async function handleUpdateShipment(newStatus) {
    try {
      const data = await orderApi.updateShipment(id, { status: newStatus });
      setShipment(data.shipment);
      setNotice({ type: "success", text: "Shipment status updated!" });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    try {
      await orderApi.updateAdminOrder({
        order_id: Number(id),
        status: form.status,
        estimated_arrival: form.estimated_arrival,
      });
      setNotice({ type: "success", text: "Order status updated successfully!" });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-header">
          <h1>Order #{order?.order_number}</h1>
          <Link to="/admin-orders" className="btn btn-secondary">Back to Orders</Link>
        </div>
        <Message notice={notice} />

        {order && (
          <div className="order-detail">
            <div className="detail-section">
              <h2>Customer Information</h2>
              <div className="info-grid">
                <div className="info-item"><label>Customer Email:</label><span>{order.user_email || order.user?.email}</span></div>
                <div className="info-item"><label>Customer Name:</label><span>{order.user_name || `${order.user?.first_name || ""} ${order.user?.last_name || ""}`.trim()}</span></div>
                <div className="info-item"><label>Order Date:</label><span>{new Date(order.created_at).toLocaleString()}</span></div>
              </div>
            </div>

            <div className="detail-section">
              <h2>Order Items</h2>
              <table className="admin-table">
                <thead><tr><th>Product</th><th>Quantity</th><th>Price</th><th>Subtotal</th></tr></thead>
                <tbody>
                  {(order.items || []).map((item, index) => (
                    <tr key={index}>
                      <td>{item.product_name || item.product?.name}</td>
                      <td>{item.quantity}</td>
                      <td>${Number(item.price).toFixed(2)}</td>
                      <td>${(Number(item.price) * Number(item.quantity)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="order-total"><strong>Total: ${Number(order.total_price).toFixed(2)}</strong></div>
            </div>

            <div className="detail-section">
              <h2>Delivery Information</h2>
              <div className="info-grid">
                <div className="info-item"><label>Delivery Address:</label><span>{order.delivery_address}</span></div>
              </div>
            </div>

            <div className="detail-section">
              <h2>Update Order Status</h2>
              <form onSubmit={handleSubmit} className="form-group-inline">
                <div className="form-group">
                  <label htmlFor="status">Order Status</label>
                  <select id="status" className="form-control" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="paid">Paid</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="estimated_arrival">Estimated Arrival Date</label>
                  <input type="datetime-local" id="estimated_arrival" className="form-control" value={form.estimated_arrival} onChange={(e) => setForm({ ...form, estimated_arrival: e.target.value })} />
                </div>

                <button type="submit" className="btn btn-primary">Update Status</button>
              </form>
            </div>
            <div className="detail-section">
              <h2>Shipment</h2>
              {shipment === undefined && <p>Loading...</p>}
              {shipment === null && order?.status !== "paid" && (
                <p>No shipment yet. Set the order status to <strong>Paid</strong> to enable shipment creation.</p>
              )}
              {shipment === null && order?.status === "paid" && (
                <div>
                  <p>No shipment created yet. The delivery address below will be used.</p>
                  <p><strong>Address:</strong> {order.delivery_address}</p>
                  <button className="btn btn-primary" onClick={handleCreateShipment}>Create Shipment</button>
                </div>
              )}
              {shipment && (
                <div>
                  <div className="info-grid">
                    <div className="info-item"><label>Shipment ID:</label><span>#{shipment.id}</span></div>
                    <div className="info-item"><label>Address:</label><span>{shipment.address}</span></div>
                    <div className="info-item"><label>Assigned Staff:</label><span>{shipment.staff_email}</span></div>
                    <div className="info-item"><label>Status:</label><span style={{ textTransform: "capitalize" }}>{shipment.status}</span></div>
                    <div className="info-item"><label>Created:</label><span>{new Date(shipment.created_at).toLocaleString()}</span></div>
                  </div>
                  <div className="form-group" style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
                    <label htmlFor="shipment_status"><strong>Update Status:</strong></label>
                    <select
                      id="shipment_status"
                      className="form-control"
                      style={{ width: "auto" }}
                      value={shipment.status}
                      onChange={(e) => handleUpdateShipment(e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="shipping">Shipping</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
